import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Box,
  Alert,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import { AdminConfirmModalProps } from '../../../types/admin';

/**
 * AdminConfirmModal - Confirmation dialog for admin actions
 *
 * Features:
 * - Severity-based styling (error, warning, info)
 * - Optional typed confirmation (e.g., "DELETE")
 * - Consequence list display
 * - Loading state during action
 * - Async action support
 *
 * TODO: Add keyboard shortcuts (Enter to confirm, Esc to cancel)
 * TODO: Add auto-focus on confirmation input
 */
export default function AdminConfirmModal({
  open,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  severity = 'warning',
  requireTypedConfirmation = false,
  confirmationText = '',
  consequences = [],
  onConfirm,
  onCancel,
  isLoading = false,
}: AdminConfirmModalProps) {
  const [typedConfirmation, setTypedConfirmation] = useState('');

  const handleConfirm = async () => {
    await onConfirm();
    setTypedConfirmation('');
  };

  const handleCancel = () => {
    setTypedConfirmation('');
    onCancel();
  };

  const isConfirmDisabled =
    isLoading ||
    (requireTypedConfirmation && typedConfirmation !== confirmationText);

  const getSeverityIcon = () => {
    switch (severity) {
      case 'error':
        return <ErrorIcon color="error" />;
      case 'warning':
        return <WarningIcon color="warning" />;
      case 'info':
        return <InfoIcon color="info" />;
      default:
        return null;
    }
  };

  const getSeverityColor = () => {
    switch (severity) {
      case 'error':
        return 'error';
      case 'warning':
        return 'warning';
      case 'info':
        return 'info';
      default:
        return 'primary';
    }
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {getSeverityIcon()}
          {title}
        </Box>
      </DialogTitle>

      <DialogContent>
        {typeof message === 'string' ? (
          <Typography variant="body1" sx={{ mb: 2 }}>
            {message}
          </Typography>
        ) : (
          message
        )}

        {consequences.length > 0 && (
          <Alert severity={severity} sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight="bold" gutterBottom>
              This will:
            </Typography>
            <List dense>
              {consequences.map((consequence, index) => (
                <ListItem key={index} sx={{ py: 0 }}>
                  <ListItemText primary={`• ${consequence}`} />
                </ListItem>
              ))}
            </List>
          </Alert>
        )}

        {requireTypedConfirmation && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" gutterBottom>
              Type <strong>{confirmationText}</strong> to confirm:
            </Typography>
            <TextField
              fullWidth
              value={typedConfirmation}
              onChange={(e) => setTypedConfirmation(e.target.value)}
              placeholder={confirmationText}
              disabled={isLoading}
              autoFocus
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCancel} disabled={isLoading}>
          {cancelText}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color={getSeverityColor()}
          disabled={isConfirmDisabled}
        >
          {isLoading ? 'Processing...' : confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
